import { _js } from '@ifixit/utils/localize';
import { SentryError } from '@ifixit/sentry';
import { refusalMessage } from 'Shared/web_api_client';
import { getCropStatus, type ImageType, postCrop } from './image_edit_api';

/**
 * The crop route queues a render and answers with a job id, and its status
 * route answers `{ status: 'in_progress' }` until the job ends.
 */

const FIRST_POLL_MS = 1500;
const POLL_BACKOFF = 1.1;
const GIVE_UP_MS = 60_000;

type JobStatus = { status?: string; error?: string };

/** Queue the render of the edited image; resolves with the job id. */
export async function startCropJob(
   type: ImageType,
   imageid: number,
   markup: string,
   ratio: string | null
): Promise<string> {
   try {
      return await postCrop(type, imageid, markup, ratio);
   } catch (error) {
      throw new SentryError(refusalMessage(error) ?? _js('The crop did not save. Try again.'));
   }
}

/**
 * Wait for the render. Resolves with the new image's Media Manager data,
 * checked against `accepts` (the same collection the dialog cropped for).
 */
export function waitForCropJob<T extends JobStatus>(
   type: ImageType,
   jobID: string,
   accepts: string | null,
   wait: (ms: number) => Promise<void> = sleep
): Promise<T> {
   const poll = async (delay: number, waited: number): Promise<T> => {
      if (waited >= GIVE_UP_MS) {
         throw new SentryError(_js('Image processing timed out'));
      }
      await wait(delay);
      const result = await getCropStatus<T>(type, jobID, accepts).catch(error => {
         throw new SentryError(refusalMessage(error) ?? _js('The crop did not save. Try again.'));
      });
      if (result.status === 'in_progress') {
         return poll(delay * POLL_BACKOFF, waited + delay);
      }
      if (result.status === 'failure' || result.error) {
         throw new SentryError(result.error || _js('The crop did not save. Try again.'));
      }
      return result;
   };
   return poll(FIRST_POLL_MS, 0);
}

function sleep(ms: number): Promise<void> {
   return new Promise(resolve => {
      setTimeout(resolve, ms);
   });
}
